import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toastWarning } from "@/shared/lib/toast";
import type { AuthStore } from "@/stores/createAuthStore";

// The refresh-token cookie alone has no concept of "idle" — as long as a tab stays open,
// createAuthStore keeps silently minting new access tokens forever, even if the user
// walked away from a shared/public computer. This is the automatic logoff HIPAA's
// Security Rule calls out for PHI access, and it's independent of (shorter than) the
// refresh token's own expiry, which only bounds how long an *unused* session can be
// resumed later, not how long an open, unattended tab stays signed in.
const IDLE_TIMEOUT_MS = 20 * 60 * 1000;
const WARNING_BEFORE_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "wheel", "touchstart"] as const;

type UseAuthStore = <T>(selector: (state: AuthStore<unknown, unknown[]>) => T) => T;

/** Builds one portal's idle-logout hook — same shape as createAuthStore, one instance
 *  per portal so patient and admin timers/toasts never interfere with each other. */
export function createIdleLogout(useAuthStore: UseAuthStore, signInPath: string) {
  return function useIdleLogout() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const signOut = useAuthStore((s) => s.signOut);
    const navigate = useNavigate();
    const warnTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const logoutTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
      if (!isAuthenticated) return;

      const resetTimers = () => {
        clearTimeout(warnTimer.current);
        clearTimeout(logoutTimer.current);

        warnTimer.current = setTimeout(() => {
          toastWarning("You'll be signed out soon due to inactivity.");
        }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

        logoutTimer.current = setTimeout(async () => {
          await signOut();
          toastWarning("You were signed out after being idle, for your security.");
          navigate(signInPath, { replace: true });
        }, IDLE_TIMEOUT_MS);
      };

      resetTimers();
      ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimers));

      return () => {
        clearTimeout(warnTimer.current);
        clearTimeout(logoutTimer.current);
        ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimers));
      };
    }, [isAuthenticated, signOut, navigate]);
  };
}
